import React, { useEffect, useState } from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import Contestant from '../../../_types/Contestant';
import NewGameState from '../../_types/NewGameState';
import styled from '@emotion/styled';
import { GamifyIcon } from '../../../../../components/GamifyIcon';
import { useTheme } from '../../../../../context/PermissionsContext/ThemeContext/ThemeContext';
import {
  ActionButton,
  ButtonGroup,
} from '../../custom-inputs/CustomInputs.styles';

interface ThemeProps {
  theme?: any;
  isDark?: boolean;
}

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderSection = styled(Box)<ThemeProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px 16px 8px;
  border-bottom: 1px solid
    ${({ theme, isDark }) => theme?.stroke || (isDark ? '#404040' : '#eaeaea')};
`;

const HeaderTitle = styled(Typography)<ThemeProps>`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme, isDark }) => theme?.text || (isDark ? '#FFFFFF' : '#333')};
  font-family: Manrope;
`;

const SeedRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 8px;
`;

const SeedBadge = styled(Box)<ThemeProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ theme, isDark }) =>
    theme?.background3 || (isDark ? '#2D2D2D' : '#f0f0f3')};
  flex-shrink: 0;
  border: 1px solid
    ${({ theme, isDark }) =>
      theme?.stroke || (isDark ? '#404040' : 'transparent')};
`;

const SeedNumber = styled(Typography)<ThemeProps>`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme, isDark }) =>
    theme?.textLight || (isDark ? '#B0B0B0' : '#666')};
  font-family: Manrope;
`;

const ContestantCardContainer = styled(Box)`
  flex-grow: 1;
`;

const ContestantCard = styled(Box)<{ selected: boolean } & ThemeProps>`
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${({ selected, theme, isDark }) => {
    if (selected) {
      return theme?.background2 || (isDark ? '#1E1E1E' : '#f8f9fa');
    }
    return theme?.background1 || (isDark ? '#121212' : 'white');
  }};
  border: 1px solid
    ${({ selected, theme, isDark }) => {
      if (selected) {
        return theme?.stroke || (isDark ? '#404040' : '#e0e0e0');
      }
      return theme?.stroke || (isDark ? '#404040' : 'transparent');
    }};
  box-shadow: ${({ selected, isDark }) => {
    if (selected) {
      return isDark
        ? '0 2px 4px rgba(0,0,0,0.3)'
        : '0 2px 4px rgba(0,0,0,0.05)';
    }
    return 'none';
  }};

  &:hover {
    background-color: ${({ selected, theme, isDark }) => {
      if (selected) {
        return theme?.background3 || (isDark ? '#2D2D2D' : '#f0f0f3');
      }
      return theme?.background2 || (isDark ? '#1E1E1E' : '#fafafa');
    }};
    border-color: ${({ theme, isDark }) =>
      theme?.primary || (isDark ? '#FF6A00' : '#e0e0e0')};
    transform: translateY(-2px);
    box-shadow: ${({ isDark }) =>
      isDark
        ? '0 4px 8px rgba(0, 0, 0, 0.3)'
        : '0 4px 8px rgba(0, 0, 0, 0.05)'};
  }

  &:active {
    transform: translateY(0);
  }
`;

const EmptyContestantCard = styled(Box)<ThemeProps>`
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${({ theme, isDark }) =>
    theme?.background1 || (isDark ? '#121212' : 'white')};
  border: 1px dashed
    ${({ theme, isDark }) => theme?.stroke || (isDark ? '#404040' : '#ccc')};

  &:hover {
    background-color: ${({ theme, isDark }) =>
      theme?.background2 || (isDark ? '#1E1E1E' : '#fafafa')};
    border-color: ${({ theme, isDark }) =>
      theme?.primary || (isDark ? '#FF6A00' : '#aaa')};
    transform: translateY(-2px);
    box-shadow: ${({ isDark }) =>
      isDark
        ? '0 4px 8px rgba(0, 0, 0, 0.3)'
        : '0 4px 8px rgba(0, 0, 0, 0.05)'};
  }

  &:active {
    transform: translateY(0);
  }
`;

const AvatarContainer = styled(Box)`
  position: relative;
  margin-right: 16px;
`;

const ContestantInfo = styled(Box)`
  display: flex;
  flex-direction: column;
`;

const ContestantName = styled(Typography)<ThemeProps>`
  font-weight: 500;
  font-size: 15px;
  color: ${({ theme, isDark }) => theme?.text || (isDark ? '#FFFFFF' : '#333')};
  font-family: Manrope;
`;

const ContestantSubtitle = styled(Typography)<ThemeProps>`
  font-size: 13px;
  color: ${({ theme, isDark }) =>
    theme?.textLight || (isDark ? '#B0B0B0' : '#888')};
  margin-top: 2px;
  font-family: Manrope;
`;

const PlusIconWrapper = styled(Box)<ThemeProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme, isDark }) =>
    theme?.background3 || (isDark ? '#2D2D2D' : '#f0f0f3')};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 16px;
  border: 1px solid
    ${({ theme, isDark }) =>
      theme?.stroke || (isDark ? '#404040' : 'transparent')};
`;

const SubmitButtonContainer = styled(Box)<ThemeProps>`
  margin-top: 24px;
  padding-top: 16px;
  background-color: ${({ theme, isDark }) =>
    isDark ? theme?.background1 || '#121212' : theme?.background1 || '#fff'};

  transition: background-color 0.2s, border-color 0.2s;
`;

interface CustomSeedProps {
  onClickAdd: (args: { seedIndex: number }) => void;
  contestants: Contestant[];
  newGameState: NewGameState;
  seedIndex: number;
  theme?: any;
  isDark?: boolean;
}

const CustomSeed: React.FC<CustomSeedProps> = ({
  onClickAdd,
  contestants,
  newGameState,
  seedIndex,
  theme,
  isDark,
}) => {
  const [contestantData, setContestantData] = useState<Contestant | null>(null);

  useEffect(() => {
    const contestantID = newGameState.competitors[seedIndex];

    if (contestantID) {
      const data = contestants.find(
        (contestant) => contestant.value === contestantID
      );

      setContestantData(data || null);
    } else {
      setContestantData(null);
    }
  }, [seedIndex, contestants, newGameState.competitors]);

  if (contestantData) {
    return (
      <ContestantCard
        onClick={() => onClickAdd({ seedIndex })}
        selected={true}
        theme={theme}
        isDark={isDark}
      >
        <AvatarContainer>
          {contestantData.avatar ? (
            <Avatar
              src={contestantData.avatar}
              sx={{ height: '48px', width: '48px' }}
            />
          ) : (
            <Avatar
              sx={{
                height: '48px',
                width: '48px',
                backgroundColor:
                  theme?.background3 || (isDark ? '#404040' : '#868686'),
                color: theme?.text || (isDark ? '#FFFFFF' : '#333'),
              }}
            >
              {contestantData.label[0].toUpperCase()}
            </Avatar>
          )}
        </AvatarContainer>
        <ContestantInfo>
          <ContestantName theme={theme} isDark={isDark}>
            {contestantData.label}
          </ContestantName>
          <ContestantSubtitle theme={theme} isDark={isDark}>
            Tap to change
          </ContestantSubtitle>
        </ContestantInfo>
      </ContestantCard>
    );
  }

  return (
    <EmptyContestantCard
      onClick={() => onClickAdd({ seedIndex })}
      theme={theme}
      isDark={isDark}
    >
      <PlusIconWrapper theme={theme} isDark={isDark}>
        <GamifyIcon icon="add" color="white" height={20} width={20} />
      </PlusIconWrapper>
      <ContestantInfo>
        <ContestantName
          theme={theme}
          isDark={isDark}
          sx={{
            color: theme?.textLight || (isDark ? '#B0B0B0' : '#888'),
          }}
        >
          Add contestant
        </ContestantName>
        <ContestantSubtitle theme={theme} isDark={isDark}>
          Tap to select
        </ContestantSubtitle>
      </ContestantInfo>
    </EmptyContestantCard>
  );
};

interface Props {
  totalNumberOfContestants: any;
  onClickAdd: (args: { seedIndex: number }) => void;
  contestants: Contestant[];
  newGameState: NewGameState;
  onSubmit?: () => void;
}

const SelectBracketContestants: React.FC<Props> = ({
  totalNumberOfContestants,
  onClickAdd,
  contestants,
  newGameState,
  onSubmit,
}) => {
  const { theme } = useTheme();
  const isDark = theme.isDark;

  const [seedsArray, setSeedsArray] = useState<Array<unknown>>([]);

  useEffect(() => {
    setSeedsArray(Array.from({ length: totalNumberOfContestants }, () => null));
  }, [totalNumberOfContestants]);

  const renderHeader = () => (
    <HeaderSection theme={theme as any} isDark={isDark}>
      <HeaderTitle theme={theme as any} isDark={isDark}>
        Configure your bracket
      </HeaderTitle>
      <Typography
        variant="caption"
        sx={{
          color: theme?.textLight || (isDark ? '#B0B0B0' : '#888'),
          fontFamily: 'Manrope',
        }}
      >
        {newGameState.competitors.filter(Boolean).length} of{' '}
        {totalNumberOfContestants} contestants added
      </Typography>
    </HeaderSection>
  );

  const renderSeeds = () =>
    seedsArray.map((_, index) => (
      <SeedRow key={index}>
        <SeedBadge theme={theme as any} isDark={isDark}>
          <SeedNumber theme={theme as any} isDark={isDark}>
            {index + 1}
          </SeedNumber>
        </SeedBadge>
        <ContestantCardContainer>
          <CustomSeed
            onClickAdd={onClickAdd}
            contestants={contestants}
            newGameState={newGameState}
            seedIndex={index}
            theme={theme as any}
            isDark={isDark}
          />
        </ContestantCardContainer>
      </SeedRow>
    ));

  const renderSubmitButton = () => (
    <SubmitButtonContainer theme={theme as any} isDark={isDark}>
      <ButtonGroup theme={theme as any} isDark={isDark}>
        <ActionButton variant="primary" theme={theme as any} onClick={onSubmit}>
          Submit
        </ActionButton>
      </ButtonGroup>
    </SubmitButtonContainer>
  );

  return (
    <Container>
      {renderHeader()}
      {renderSeeds()}
      {renderSubmitButton()}
    </Container>
  );
};

export default SelectBracketContestants;
